import { useState, useEffect, useCallback } from 'react';
import type { ReactNode, ComponentPropsWithoutRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import type { EmblaOptionsType } from 'embla-carousel-react';
import useTranslation from 'next-translate/useTranslation';
import clsx from 'clsx';

//components
import Icon from '@/components/base/Icon';

export const testCarouselImg = [
    { src: '/images/carousel/public/new-ico-image@2x.png' },
    { src: '/images/carousel/public/twitter-image@2x.png' },
    { src: '/images/themes/ico-theme-dark.png' },
    { src: '/images/themes/ico-theme-light.png' }
];

const DEFAULT = 'default';
const DARKEN = 'darken';

type NavPositionType = 'classic' | 'external' | 'dot';

export type CarouselProps = ComponentPropsWithoutRef<'div'> & {
    children: ReactNode;
    config?: EmblaOptionsType;
    hasDots?: boolean;
    hasCounter?: boolean;
    hasNavigation?: boolean;
    navPosition?: NavPositionType;
    navOnHover?: boolean;
    navCallback?: () => void;
    dotVariant?: typeof DEFAULT | typeof DARKEN;
    speed?: number;
    activeIndex?: number;
    hideCounterHover?: boolean;
    updateActiveIndex?: (index: number) => void;
    onSettle?: () => void;
    fullHeight?: boolean;
    disableReInit?: boolean;
};

type directionType = 'left' | 'right';

const Carousel = ({
    children,
    config,
    dotVariant = DEFAULT,
    hasDots = false,
    speed = 25,
    hasCounter = false,
    hasNavigation = true,
    navPosition = 'classic',
    navOnHover = false,
    activeIndex = null,
    hideCounterHover = false,
    updateActiveIndex = (index) => {
        return index;
    },
    navCallback,
    onSettle,
    fullHeight = false,
    className,
    disableReInit = false,
    ...restProps
}: CarouselProps) => {
    const { t } = useTranslation('commons');
    const [viewportRef, embla] = useEmblaCarousel({
        align: 'start',
        speed,
        draggable: false,
        containScroll: 'trimSnaps',
        ...config
    });
    const [prevBtnEnabled, setPrevBtnEnabled] = useState(false);
    const [nextBtnEnabled, setNextBtnEnabled] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

    const previous = useCallback(() => embla && embla.scrollPrev(), [embla]);
    const next = useCallback(() => embla && embla.scrollNext(), [embla]);

    //scrollTo slide when click on dot
    const scrollTo = useCallback((index) => embla && embla.scrollTo(index), [embla]);
    // get the number of visible slide to determine the number of slide to scroll
    const visibleSlides = embla?.slidesInView()?.length ?? 1;
    //get the total of slides in the carousel
    const nbSlides = embla?.slideNodes()?.length;

    const onSelect = useCallback(() => {
        if (!embla) return;
        setScrollSnaps(embla.scrollSnapList());
        setSelectedIndex(embla.selectedScrollSnap());
        setPrevBtnEnabled(embla.canScrollPrev());
        setNextBtnEnabled(embla.canScrollNext());
    }, [embla]);

    const navCallbackLaunch = () => {
        if (typeof navCallback === 'function') {
            navCallback();
        }
    };

    useEffect(() => {
        if (!embla) return;
        onSelect();
        let settleBind = undefined;
        if (typeof onSettle === 'function') {
            settleBind = onSettle.bind(this, embla);
            embla.on('settle', settleBind);
        }
        embla.on('select', onSelect);

        () => {
            embla.off('select', onSelect);
            if (typeof onSettle === 'function' && settleBind) {
                embla.off('settle', settleBind);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [embla, nbSlides]);

    // remount carousel to make sure carousel render correctly if data render from cache (navigation button pb)
    useEffect(() => {
        if (disableReInit) return;
        if (!embla) return;
        embla.reInit({
            align: 'start',
            containScroll: 'trimSnaps',
            slidesToScroll: process.env.NODE_ENV === 'test' ? 1 : visibleSlides, // pass static value in test to prevent JavaScript heap out of memory
            ...config
        });
        onSelect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [embla, nbSlides, children, config, visibleSlides]);

    useEffect(() => {
        updateActiveIndex(selectedIndex);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedIndex]);

    useEffect(() => {
        if (activeIndex !== null && activeIndex !== selectedIndex) {
            scrollTo(activeIndex);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeIndex]);

    return (
        <>
            <div
                className={clsx('relative', {
                    group: navOnHover || hideCounterHover,
                    'min-h-0 flex-1': fullHeight
                })}>
                <div
                    className={clsx('embla relative overflow-hidden', {
                        'h-full': fullHeight
                    })}
                    {...restProps}>
                    {hasCounter ? (
                        <div
                            className={clsx(
                                'absolute right-2.5 top-2.5 z-1 flex min-w-[2.375rem] justify-center rounded-m bg-white p-1 text-xs text-icoGray-500 dark:bg-icoDarkMode-anthracite',
                                {
                                    'group-hover:hidden': hideCounterHover
                                }
                            )}>
                            {selectedIndex + 1}/{scrollSnaps.length}
                        </div>
                    ) : null}
                    <div
                        className={clsx('embla__viewport', {
                            'h-full': fullHeight
                        })}
                        ref={viewportRef}>
                        <div
                            className={clsx('embla__container flex w-full space-x-2', className, {
                                'h-full': fullHeight
                            })}>
                            {children}
                        </div>
                    </div>
                </div>
                {hasNavigation && navPosition !== 'dot' ? (
                    <>
                        <Carousel.NavButton
                            aria-label={t('previous')}
                            onClick={(e) => {
                                e.stopPropagation();
                                previous();
                                navCallbackLaunch();
                            }}
                            position={navPosition}
                            disabled={!prevBtnEnabled}
                            direction="left"
                            className={clsx('carousel-nav-prev', {
                                'hidden group-hover:inline-flex': navOnHover && prevBtnEnabled
                            })}
                        />
                        <Carousel.NavButton
                            aria-label={t('next')}
                            onClick={(e) => {
                                e.stopPropagation();
                                next();
                                navCallbackLaunch();
                            }}
                            position={navPosition}
                            disabled={!nextBtnEnabled}
                            direction="right"
                            className={clsx('carousel-nav-next', {
                                'hidden group-hover:inline-flex': navOnHover && nextBtnEnabled
                            })}
                        />
                    </>
                ) : null}
            </div>
            {hasDots ? (
                <div className="mt-5 flex items-center justify-center space-x-4">
                    {hasNavigation && navPosition === 'dot' ? (
                        <NavDotButton
                            aria-label={t('previous')}
                            onClick={() => {
                                previous();
                                navCallbackLaunch();
                            }}
                            disabled={!prevBtnEnabled}
                            direction="left"
                            className="ml-5"
                        />
                    ) : null}
                    {scrollSnaps.map((_, index) => (
                        <DotButton
                            dotVariant={dotVariant}
                            aria-label={`dot-${index}`}
                            key={index}
                            selected={index === selectedIndex}
                            onClick={() => {
                                scrollTo(index);
                                navCallbackLaunch();
                            }}
                        />
                    ))}
                    {hasNavigation && navPosition === 'dot' ? (
                        <NavDotButton
                            aria-label={t('previous')}
                            onClick={() => {
                                next();
                                navCallbackLaunch();
                            }}
                            disabled={!nextBtnEnabled}
                            direction="right"
                            className="ml-5"
                        />
                    ) : null}
                </div>
            ) : null}
        </>
    );
};

const Slide = ({
    children,
    className,
    size,
    ...restProps
}: ComponentPropsWithoutRef<'div'> & {
    size: 'full' | '50%';
}) => {
    return (
        <div
            className={clsx(
                'embla__slide relative flex items-center justify-center',
                {
                    'min-w-full': size === 'full',
                    'min-w-1/2': size === '50%'
                },
                className
            )}
            {...restProps}>
            {children}
        </div>
    );
};
Carousel.Slide = Slide;

const NavButton = ({
    disabled,
    direction,
    position,
    onClick,
    className,
    ...restProps
}: ComponentPropsWithoutRef<'button'> & {
    direction: directionType;
    position: NavPositionType;
}) => {
    return (
        <button
            className={clsx(
                'absolute',
                {
                    'left-1 top-1/2 -translate-y-1/2 transform':
                        direction === 'left' && position === 'classic',
                    'right-1 top-1/2 -translate-y-1/2 transform':
                        direction === 'right' && position === 'classic',
                    '-left-6 top-1/2 -translate-y-1/2 transform':
                        direction === 'left' && position === 'external',
                    '-right-6 top-1/2 -translate-y-1/2 transform':
                        direction === 'right' && position === 'external',
                    hidden: disabled
                },
                'inline-flex items-center justify-center rounded-full border p-4 text-sm text-icoGray-500 shadow-icoM backdrop-blur-md',
                // Colors
                'border-white bg-white/80 dark:border-icoDarkMode-deepBlack dark:bg-icoDarkMode-deepBlack/40',
                // Firefox
                'firefox:bg-gradient-to-tr firefox:from-icoGrayFirefox-300 firefox:to-icoWhiteFirefox dark:firefox:bg-icoDarkMode-deepBlack dark:firefox:bg-opacity-90 dark:firefox:bg-none',
                className
            )}
            disabled={disabled}
            onClick={onClick}
            {...restProps}>
            <Icon icon={`arrow-${direction}`} />
        </button>
    );
};
Carousel.NavButton = NavButton;

const DotButton = ({
    selected,
    onClick,
    dotVariant,
    ...restProps
}: ComponentPropsWithoutRef<'button'> & {
    selected: boolean;
    dotVariant: typeof DEFAULT | typeof DARKEN;
}) => {
    return (
        <button
            className={clsx('embla__dot h-1.5 w-1.5 rounded-full', {
                'bg-icoGray-500 dark:bg-icoBlue': selected && dotVariant === DEFAULT,
                'bg-icoBlue': selected && dotVariant === DARKEN,
                'bg-icoGray-300 dark:bg-icoDarkMode-wolf': !selected && dotVariant === DEFAULT,
                'bg-icoGray-400 dark:bg-icoDarkMode-wolf': !selected && dotVariant === DARKEN
            })}
            type="button"
            onClick={onClick}
            {...restProps}
        />
    );
};

const NavDotButton = ({
    disabled = false,
    direction,
    onClick,
    className = '',
    ...restProps
}: ComponentPropsWithoutRef<'button'> & { direction: directionType }) => {
    const { t } = useTranslation('commons');

    return (
        <button
            aria-label={direction === 'left' ? t('previous') : t('next')}
            onClick={onClick}
            disabled={disabled}
            className={clsx(
                'flex items-center text-xs text-icoGray-500 disabled:opacity-60 dark:text-icoDarkMode-wolf',
                className
            )}
            {...restProps}>
            <Icon icon={`chevron-${direction}`} />
        </button>
    );
};

export default Carousel;
